import { useEffect, useState } from "react";
import { UI_CONFIG, API_BASE_URL } from "../constants/index";

export default function Success({ prize, onRestart }) {
  const [isVisible, setIsVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimStatus, setClaimStatus] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleClaimPrize = async () => {
    setIsClaiming(true);
    setClaimStatus(null);
    
    try {
      // Get user data from localStorage
      const userEmail = localStorage.getItem("userEmail");
      const userName = localStorage.getItem("userFirstName");
      
      if (!userEmail || !userName) {
        setClaimStatus("error");
        return;
      }

      // Submit form to trigger email sending
      const response = await fetch(`${API_BASE_URL}/api/users/send-prize-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: userName,
          email: userEmail,
          prize: prize?.name || 'Prix gagné',
        }),
      });

      if (response.ok) {
        setClaimStatus("success");
      } else {
        setClaimStatus("error");
      }
    } catch (error) {
      console.error('Error claiming prize:', error);
      setClaimStatus("error");
    } finally {
      setIsClaiming(false);
    }
  };

  const renderLogo = () => (
    <div className="mb-6 animate-fade-in-up">
      <div className="w-24 h-24 mx-auto">
        <img 
          src="/static/juniorisep.svg" 
          alt="JUNIORISEP Logo" 
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );

  const renderSuccessIcon = () => (
    <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
      <div className="w-32 h-32 mx-auto bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
        <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
  );

  const renderPrizeDisplay = () => (
    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-200">
      <div className="text-4xl mb-3">
        <svg className="w-16 h-16 mx-auto text-orange-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-gray-800 mb-2">
        {prize?.name}
      </h3>
      <p className="text-gray-600 text-sm mb-3">
        {prize?.description}
      </p>
      {prize?.value && (
        <div className="text-green-600 font-semibold">
          Valeur : €{prize.value}
        </div>
      )}
    </div>
  );

  const renderNextSteps = () => (
    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Et Maintenant ?</h3>
      
      <div className="space-y-4 text-left">
        <div className="flex items-start">
          <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs mr-3 mt-0.5 flex-shrink-0">1</span>
          <div>
            <div className="text-gray-800 font-medium">Cliquez sur "Récupérer votre prix"</div>
            <div className="text-gray-600 text-sm">Nous vous enverrons un email avec toutes les instructions</div>
          </div>
        </div>
        
        <div className="flex items-start">
          <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs mr-3 mt-0.5 flex-shrink-0">2</span>
          <div>
            <div className="text-gray-800 font-medium">Vérifiez Votre Email</div>
            <div className="text-gray-600 text-sm">Consultez votre boîte de réception pour les détails</div>
          </div>
        </div>
        
        <div className="flex items-start">
          <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs mr-3 mt-0.5 flex-shrink-0">3</span>
          <div>
            <div className="text-gray-800 font-medium">Récupérez Votre Prix</div>
            <div className="text-gray-600 text-sm">Présentez-vous à notre bureau avec une pièce d'identité</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActionButtons = () => (
    <div className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
      <button
        className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        onClick={handleClaimPrize}
        disabled={isClaiming}
        aria-label="Récupérer votre prix"
      >
        {isClaiming ? (
          <svg className="w-6 h-6 inline mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ) : (
          <svg className="w-6 h-6 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2a2 2 0 01-2-2z" />
          </svg>
        )}
        {isClaiming ? 'Envoi en cours...' : 'Récupérer votre prix'}
      </button>

      {isClaiming && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-blue-800 text-sm font-medium">
            📧 Envoi de l'email en cours...
          </p>
        </div>
      )}

      {claimStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="text-green-800 text-sm font-medium">
            ✅ Email envoyé avec succès ! Vérifiez votre boîte de réception pour les instructions de récupération.
          </p>
        </div>
      )}

      {claimStatus === 'error' && (
        <>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-red-800 text-sm font-medium">
              ❌ Erreur lors de l'envoi. Veuillez réessayer ou nous contacter.
            </p>
          </div>
          <button
            onClick={handleClaimPrize}
            className="w-full bg-red-500 text-white font-medium py-2 px-4 rounded-lg hover:bg-red-600 transition-colors text-sm"
          >
            Réessayer
          </button>
        </>
      )}
      
      <button
        className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        onClick={onRestart}
        aria-label="Recommencer et tourner la roue à nouveau"
      >
        <svg className="w-6 h-6 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Tourner à Nouveau
      </button>
      
      <p className="text-gray-500 text-sm">
        Partagez cette expérience avec vos amis !
      </p>
    </div>
  );

  const renderFloatingElements = () => (
    <>
      <div className="absolute top-20 left-10 text-2xl animate-bounce" style={{ animationDelay: '0.5s' }}>
        <svg className="w-8 h-8 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
        </svg>
      </div>
      
      <div className="absolute top-40 right-16 text-2xl animate-bounce" style={{ animationDelay: '1s' }}>
        <svg className="w-8 h-8 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838l-2.727 1.17 1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
        </svg>
      </div>
      
      <div className="absolute bottom-40 left-16 text-2xl animate-bounce" style={{ animationDelay: '1.5s' }}>
        <svg className="w-8 h-8 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </div>
    </>
  );

  return (
    <div className="w-full h-screen flex flex-col bg-white overflow-hidden">
      <div className="flex-1 flex flex-col justify-center px-4 py-4">
        <div className={`text-center w-full max-w-md transition-all duration-1000 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
        
        {renderLogo()}
        {renderSuccessIcon()}

        <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Félicitations !
          </h1>
          
          <p className="text-gray-600 text-lg mb-6">
            Vous avez rejoint notre équipe avec succès et récupéré votre prix !
          </p>

          {renderPrizeDisplay()}
        </div>

        <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          {renderNextSteps()}
        </div>

        {renderActionButtons()}
        </div>
      </div>

      <div className="flex-shrink-0 pb-4">
        <div className="flex justify-center space-x-4 animate-fade-in-up" style={{ animationDelay: '1s' }}>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {showConfetti && renderFloatingElements()}

        <div className="mt-4 pt-4 border-t border-gray-200 animate-fade-in-up" style={{ animationDelay: '1.2s' }}>
          <p className="text-gray-500 text-xs text-center">
            Créé par <span className="text-orange-600 font-semibold">JUNIORISEP</span>
          </p>
        </div>
      </div>
    </div>
  );
}
